import React, { useState, useEffect } from "react";
import BackendLayout from "../../layouts/BackendLayout";
import Pagination from "../../components/Pagination";
import Table from "../../components/table/Table";
import Select2 from "../../components/Select2";
import { useDispatch, useSelector } from "react-redux";
import { useDocumentTitle, usePagination, useSort } from "../../hooks";
import { getRouteBreadcrumb } from "../../services/BreadcrumbServices";
import { MACHINE_MASTER_UPDATE } from "../../constants/routes";
import { getMachineUpdatePageSettings } from "../../api/setting/pageApi";
import { setPageSettings } from "../../store/actions/pageActions";
import { PageTitle } from "../../components/admin";
import {
  FormGroupInput,
  FormGroup,
  Label,
  TextInput,
  ButtonFilter,
  ButtonAdd,
  ButtonReset,
  ButtonUpdate,
  CheckboxInput,
  ButtonDPad,
  RedirectAlert,
} from "../../components";
import {SelectUtils, hasErrorInput} from "../../utils";
import {
  setAlertError,
  resetAlert,
  setAlertSuccess,
  setUpdateMachineBatchAlertError,
  setPageLoading,
  clearPageLoading,
} from "../../store/actions";
import {
  getMachineGroupList,
  getMachineAddFormData,
  getMachineOptions,
  getFilterGroupList,
  getFirstSelectedGroup,
  getFilterDataValid,
  getTableGroupOptions,
} from "../../services/MachineServices";
import { useGetMachines } from "../../hooks/MachineHook";
import {
  MachineToggle,
  MachineAddModal,
} from "../../components/machine-master";
import { addMachine, updateMachines } from "../../api/machine/machineUpdateApi";
import PageAlert from "../../components/alerts/PageAlert";
import { errorStatusHandle } from "../../utils/error-utils";
import ConfirmModal from "../../components/ConfirmModal";
import { range } from "../../utils/array-utils";
import {
  handleOnBeforeUnload,
  smoothScrollToTop,
} from "../../utils/common-utils";
import {
  setButtonProcessing,
  clearButtonProcessing,
} from "../../store/actions";
import {
  BUTTON_FILTER_MACHINE_UPDATE,
  BUTTON_ADD_MACHINE_UPDATE,
  BUTTON_UPDATE_MACHINE_UPDATE,
} from "../../constants/buttons";
import LoadingSpinner from "../../components/loading/LoadingSpinner";
import { USER_SETTING_CATEGORIES } from "../../constants/app";
import { updateUserSetting } from "../../services/user-master/UserSettingServices";
import {MACHINE_MASTER_UPDATE_PAGE} from "../../constants/pageName";
import useRedirectPage from "../../hooks/useRedirectPage";

export default function MachineUpdatePage() {
  useRedirectPage(MACHINE_MASTER_UPDATE_PAGE);
  const dispatch = useDispatch();
  useDocumentTitle("設備マスタ更新 - 点検王");
  const breadcrumb = getRouteBreadcrumb(MACHINE_MASTER_UPDATE);
  const groupSettings = useSelector(
    (state) => state.app_settings.machine_group
  );

  const [machineOptions, setMachineOptions] = useState();
  const [groupsList, setGroupsList] = useState({});
  const [groupsChildren, setGroupsChildren] = useState({});
  const [filterOptions, setFilterOptions] = useState({});
  const [tableGroupOptions, setTableGroupOptions] = useState([]);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [isModalMessage, setIsModalMessage] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [errorsModal, setErrorsModal] = useState({});
  const [addData, setAddData] = useState({});
  const [updateData, setUpdateData] = useState({});
  const [deleteIds, setDeleteIds] = useState([]);
  const [hasChanged, setHasChanged] = useState(false);
  const [machines, setMachines] = useState([]);
  const [initGroupOptions, setInitGroupOptions] = useState([]);
  const [firstSelected, setFirstSelected] = useState(0);

  useEffect(() => {
    setMachineUpdatePageSettings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const {
    errors,
    filterData,
    initialDataSort,
    reload,
    originalData,
    setErrors,
    setFilterData,
    setReload,
    setGetMachineData,
  } = useGetMachines();
  const { sortData, sortOrderList, handleSortColumn } =
    useSort(initialDataSort);

  const setMachineUpdatePageSettings = async (e) => {
    dispatch(setPageLoading());
    const pageSettings = await getMachineUpdatePageSettings();
    let initFilterData = {
      group_1: "",
      group_2: "",
      group_3: "",
      group_4: "",
      group_5: "",
      machine_id: null,
    };
    if (pageSettings.user_setting?.setting) {
      initFilterData = getFilterDataValid(
        pageSettings.user_setting.setting,
        pageSettings.input_options,
        "machine"
      );
    }
    setFilterData(initFilterData);
    setGetMachineData(initFilterData);
    const firstSelectedGroup = getFirstSelectedGroup(initFilterData);
    const groupsOptions = getFilterGroupList(pageSettings.input_options);
    if (initFilterData !== 0) {
      setFirstSelected(firstSelectedGroup);
      setFilterOptions(
        SelectUtils.getChildGroupOptionsFilter(
          pageSettings.input_options,
          groupsOptions,
          initFilterData,
          firstSelectedGroup
        )
      );
    } else {
      setFilterOptions(groupsOptions);
    }
    setReload(reload + 1);
    dispatch(setPageSettings(pageSettings));
    setMachineOptions(getMachineOptions(pageSettings.input_options.machines));
    setGroupsList(getMachineGroupList(pageSettings.input_options));
    setGroupsChildren(pageSettings.groups_children);
    setInitGroupOptions(pageSettings.input_options);
    dispatch(clearPageLoading());
  };

  useEffect(() => {
    setMachines(sortData);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortData]);

  const {
    currentPage,
    setCurrentPage,
    paginatedData,
    setPaginatedData,
    pageSize,
    setPageSize,
    totalItems,
  } = usePagination(machines);

  const setFilterOptionsByCondition = (newFilterData, groupNumber) => {
    const groupOptions = SelectUtils.getChildGroupOptionsFilter(
      initGroupOptions,
      filterOptions,
      newFilterData,
      groupNumber
    );
    setFilterOptions(groupOptions);
  };

  const handleSetFilterData = (level, groupName) => {
    const newFilterData = { ...filterData };
    if (newFilterData[`group_${level}`] === groupName) return;
    newFilterData[`group_${level}`] = groupName;
    for (let i = level + 1; i < 6; i++) {
      newFilterData[`group_${i}`] = "";
    }
    setFilterData(newFilterData);

    let groupNumber = firstSelected;
    if (level <= firstSelected || firstSelected === 0) {
      groupNumber = level;
    }
    if (firstSelected === level && groupName === "") {
      groupNumber = 0;
    }
    setFirstSelected(groupNumber);
    setFilterOptionsByCondition(newFilterData, groupNumber);
  };

  const updateSettings = async () => {
    try {
      await updateUserSetting(
        filterData,
        USER_SETTING_CATEGORIES.MACHINE_UPDATE
      );
    } catch (e) {
      errorStatusHandle(e);
    }
  };

  const handleSubmitFilter = () => {
    updateSettings();
    setGetMachineData(filterData);
    dispatch(resetAlert());
    setErrors({});
    setReload(reload + 1);
  };

  //Table
  useEffect(() => {
    if (paginatedData) {
      const groupOptionsList = paginatedData.map((row) => {
        let groupCombine = {
          1: row.group_1?.id,
          2: row.group_2?.id,
          3: row.group_3?.id,
          4: row.group_4?.id,
          5: row.group_5?.id,
        };
        return getTableGroupOptions(
          groupsChildren,
          groupCombine,
          groupsList,
          originalData[row.id]
        );
      });
      setTableGroupOptions(groupOptionsList);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paginatedData, groupsChildren, groupsList]);

  //Update
  const handleSelectChange = (e, idx, groupName, row, level) => {
    setHasChanged(true);
    const rowUpdate = paginatedData.find((item) => item.id === row.id);
    if (rowUpdate[groupName] !== null && rowUpdate[groupName].id === e.value)
      return;
    setPaginatedData((prevData) => {
      const updateData = [...prevData];
      return getNewUpdateData(updateData, e, idx, groupName, level);
    });
    setMachines((prevData) => {
      const updateData = [...prevData];
      const index = updateData.findIndex((item) => item.id === row.id);
      return getNewUpdateData(updateData, e, index, groupName, level);
    });
    setUpdateFormData(e.value, groupName, row, level);
  };

  const getNewUpdateData = (data, e, index, groupName, level) => {
    if (data[index][groupName]) {
      data[index][groupName].id = e.value;
      data[index][groupName].name = e.label;
    } else {
      data[index][groupName] = {};
      data[index][groupName]["id"] = e.value;
      data[index][groupName]["name"] = e.label;
    }
    for (let i = level + 1; i < 6; i++) {
      if (
        groupSettings &&
        groupSettings[`group_${i}`]?.active &&
        data[index][`group_${i}`]
      ) {
        data[index][`group_${i}`].id = null;
      }
    }
    return data;
  };

  const handleInputChange = (value, idx, fieldName, row) => {
    setHasChanged(true);
    setPaginatedData((prevData) => {
      const updateData = [...prevData];
      updateData[idx][fieldName] = value;
      return updateData;
    });
    setUpdateFormData(value, fieldName, row);
  };

  const setUpdateFormData = (value, fieldName, row, level) => {
    setUpdateData((prevData) => {
      const newUpdateData = { ...prevData };
      if (!newUpdateData.hasOwnProperty(row.id)) {
        newUpdateData[row.id] = {
          group_1_id: row.group_1 ? row.group_1.id : null,
          group_2_id: row.group_2 ? row.group_2.id : null,
          group_3_id: row.group_3 ? row.group_3.id : null,
          group_4_id: row.group_4 ? row.group_4.id : null,
          group_5_id: row.group_5 ? row.group_5.id : null,
          name: row.name,
          status: row.status,
        };
      }
      if (fieldName.startsWith("group")) {
        fieldName = `${fieldName}_id`;
        for (let i = level + 1; i < 6; i++) {
          newUpdateData[row.id][`group_${i}_id`] = null;
        }
      }
      newUpdateData[row.id][fieldName] = value;
      return newUpdateData;
    });
  };

  const handleCheckboxChange = (id, e, index) => {
    setHasChanged(true);
    const isChecked = e.target.checked;
    const updatedDeleteIds = isChecked
      ? [...deleteIds, id]
      : deleteIds.filter((positionId) => positionId !== id);
    setDeleteIds(updatedDeleteIds);
    setPaginatedData((prevData) => {
      const updateData = [...prevData];
      updateData[index]["delete"] = isChecked;
      return updateData;
    });
  };

  const handleSubmitUpdate = () => {
    dispatch(setButtonProcessing(BUTTON_UPDATE_MACHINE_UPDATE));
    dispatch(resetAlert());
    const groupEmptyErrors = checkGroupEmpty();
    if (Object.keys(groupEmptyErrors).length > 1) {
      dispatch(setAlertError(groupEmptyErrors.error_message));
      setErrors(groupEmptyErrors.errors);
      smoothScrollToTop();
      dispatch(clearButtonProcessing(BUTTON_UPDATE_MACHINE_UPDATE));
      return;
    }
    updateMachines(updateData, deleteIds)
      .then((response) => {
        setReload(reload + 1);
        dispatch(setAlertSuccess(response.message));
        setErrors({});
        setDeleteIds([]);
        setUpdateData({});
        setHasChanged(false);
        smoothScrollToTop();
      })
      .catch((error) => {
        errorStatusHandle(error);
        if (error.status === 400) {
          let responseData = error.data;
          dispatch(setUpdateMachineBatchAlertError(responseData));
          setErrors(error.data.errors);
          smoothScrollToTop();
        }
      })
      .finally(() => {
        dispatch(clearButtonProcessing(BUTTON_UPDATE_MACHINE_UPDATE));
      });
  };

  const checkGroupEmpty = () => {
    let errorsGroupEmpty = {
      error_message: "未入力項目が存在します。",
    };
    let errorItems = {};
    let groupKeys = [];
    for (const key in groupSettings) {
      if (key !== "machine" && groupSettings[key].active) {
        groupKeys.push(key);
      }
    }
    machines.forEach((item) => {
      if (deleteIds.includes(item.id)) {
        return;
      }
      groupKeys.forEach((key) => {
        if (item[key] === null || item[key].id === null) {
          errorItems[
            `${key}_id[${item.id}]`
          ] = `${groupSettings[key].name}が未選択です。`;
        }
      });
    });
    if (Object.keys(errorItems).length !== 0) {
      errorsGroupEmpty["errors"] = errorItems;
    }
    return errorsGroupEmpty;
  };

  //Add
  const handleClickAddButton = () => {
    setAddData({
      1: null,
      2: null,
      3: null,
      4: null,
      5: null,
      name: "",
      status: "1",
    });
    setErrorMessage("");
    setErrorsModal({});
    setShowAddModal(true);
  };

  const handleSubmitAdd = () => {
    dispatch(setButtonProcessing(BUTTON_ADD_MACHINE_UPDATE));
    dispatch(resetAlert());
    setErrors({});
    addMachine(getMachineAddFormData(addData))
      .then((response) => {
        setReload(reload + 1);
        dispatch(setAlertSuccess(response.message));
        setErrorsModal({});
        setShowAddModal(false);
        setErrorMessage("");
      })
      .catch((error) => {
        errorStatusHandle(error);
        if (error.status === 400) {
          setErrorMessage(error.data.error_message);
          setErrorsModal(error.data.errors);
          setIsModalMessage(true);
        }
      })
      .finally(() => {
        dispatch(clearButtonProcessing(BUTTON_ADD_MACHINE_UPDATE));
      });
  };

  const handleButtonReset = () => {
    if (Object.keys(updateData).length > 0 || deleteIds.length > 0) {
      setHasChanged(false);
      setIsConfirmModalOpen(true);
    } else {
      window.location.reload();
    }
  };

  const renderMessages = () => {
    if (errorMessage.length > 0) {
      return <div className="error-block mb-4 !mt-0">{errorMessage}</div>;
    }
    return;
  };

  useEffect(() => {
    if (!hasChanged) return;
    window.addEventListener("beforeunload", handleOnBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", handleOnBeforeUnload);
    };
  }, [hasChanged]);

  return (
    <BackendLayout breadcrumb={breadcrumb}>
      <PageTitle title="設備マスタ更新" />
      <RedirectAlert />
      <PageAlert />
      <ButtonDPad />
      <div className="machine-update__content">
        <div className="machine-update__header inline-block pl-5 pr-[100px] border border-tenken-gray-300">
          <p className="font-light lg:mt-6 mt-5">
            選択した入力値で結果をフィルタリングしてください。
          </p>
          <div className="flex pt-2 lg:pt-3 space-x-4">
            {range(1, 5).map(
              (level) =>
                groupSettings &&
                groupSettings[`group_${level}`]?.active && (
                  <FormGroup key={level} className="" direction="vertical">
                    <Label className="mb-[4px] whitespace-nowrap">
                      {groupSettings[`group_${level}`]?.name}
                    </Label>
                    <FormGroupInput>
                      <Select2
                        options={filterOptions[`group_${level}`]}
                        value={SelectUtils.getSelectedValue(
                          filterOptions[`group_${level}`],
                          filterData[`group_${level}`],
                          true
                        )}
                        hasError={hasErrorInput(errors, `group_${level}_id`)}
                        className="lg:min-w-[216px] min-w-[180px] lg:max-w-[240px] max-w-[228px]"
                        classNamePrefix="react-select"
                        onChange={(e) => handleSetFilterData(level, e.value)}
                        maxLength="30"
                        name={`group_${level}`}
                        nextFocus={
                          groupSettings[`group_${level + 1}`]?.active
                            ? `group_${parseInt(level + 1)}_id`
                            : "machine_id_id"
                        }
                        autoFocus={level === 1}
                      />
                    </FormGroupInput>
                  </FormGroup>
                )
            )}
            <FormGroup className="" direction="vertical">
              <Label className="mb-[4px] whitespace-nowrap">
                {groupSettings["machine"].name}
              </Label>
              <FormGroupInput>
                <Select2
                  options={machineOptions}
                  value={SelectUtils.getSelectedValue(
                    machineOptions,
                    filterData["machine_id"],
                    true
                  )}
                  hasError={hasErrorInput(errors, `machine_id`)}
                  className="lg:min-w-[216px] min-w-[180px] lg:max-w-[240px] max-w-[228px]"
                  classNamePrefix="react-select"
                  onChange={(e) =>
                    setFilterData({ ...filterData, machine_id: e.value })
                  }
                  name="machine_id"
                  maxLength="200"
                />
              </FormGroupInput>
            </FormGroup>
            <FormGroup
              direction="vertical"
              className="2xl:ml-10 xl:ml-5 ml-4 lg:mt-[33px] mt-[31px]"
            >
              <ButtonFilter
                onClick={handleSubmitFilter}
                id={BUTTON_FILTER_MACHINE_UPDATE}
              />
            </FormGroup>
          </div>
        </div>
        <div className="min-w-full w-fit pr-6">
          <div className="machine-update__table border border-tenken-gray-300 w-full">
            <Table striped>
              <Table.Head>
                {range(1, 5).map(
                  (level) =>
                    groupSettings &&
                    groupSettings[`group_${level}`]?.active && (
                      <Table.HeadCell
                        key={level}
                        className="whitespace-nowrap"
                        sortable
                        order={sortOrderList[`group_${level}_name`]}
                        onChangeOrder={() =>
                          handleSortColumn(`group_${level}_name`)
                        }
                      >
                        {groupSettings[`group_${level}`]?.name}
                      </Table.HeadCell>
                    )
                )}
                <Table.HeadCell
                  sortable
                  order={sortOrderList.name}
                  onChangeOrder={() => handleSortColumn("name")}
                  className="whitespace-nowrap"
                >
                  {groupSettings["machine"].name}
                </Table.HeadCell>
                <Table.HeadCell className="whitespace-nowrap text-center">
                  状態
                </Table.HeadCell>
                <Table.HeadCell className="whitespace-nowrap text-center">
                  削除
                </Table.HeadCell>
              </Table.Head>
              <Table.Body className="devide-y text-base">
                <LoadingSpinner component="machine_update" />
                {paginatedData.map((row, idx) => (
                  <Table.Row
                    key={idx}
                    className={row.delete ? "!bg-[#FFE7E7]" : ""}
                  >
                    {range(1, 5).map(
                      (level) =>
                        groupSettings &&
                        groupSettings[`group_${level}`]?.active && (
                          <Table.Cell key={level}>
                            <Select2
                              nextFocus={
                                groupSettings[`group_${level + 1}`]?.active
                                  ? `row_${idx + 1}_group_${parseInt(
                                      level + 1
                                    )}_id`
                                  : `row_${idx + 1}_name_id`
                              }
                              name={`row_${idx + 1}_group_${level}`}
                              className="min-w-[200px] lg:max-w-[250px] max-w-[236px]"
                              classNamePrefix="react-select"
                              options={tableGroupOptions?.[idx]?.[level]}
                              value={SelectUtils.getSelectedGroupValueForTable(
                                tableGroupOptions?.[idx]?.[level],
                                row[`group_${level}`]?.id,
                                true
                              )}
                              hasError={hasErrorInput(
                                errors,
                                `group_${level}_id[${row.id}]`
                              )}
                              onChange={(e) =>
                                handleSelectChange(
                                  e,
                                  idx,
                                  `group_${level}`,
                                  row,
                                  level
                                )
                              }
                              maxLength="30"
                            />
                          </Table.Cell>
                        )
                    )}
                    <Table.Cell>
                      <TextInput
                        className="w-full min-w-[200px] lg:max-w-[250px] max-w-[236px] text-tenken-gray-900"
                        name="name"
                        id={`row_${idx + 1}_name_id`}
                        hasError={hasErrorInput(errors, `name[${row.id}]`)}
                        onChange={(e) =>
                          handleInputChange(e.target.value, idx, "name", row)
                        }
                        value={row.name || ""}
                        maxLength="200"
                      />
                    </Table.Cell>
                    <Table.Cell>
                      <MachineToggle
                        handleChangeToggle={handleInputChange}
                        checked={row.status}
                        idx={idx}
                        row={row}
                      />
                    </Table.Cell>
                    <Table.Cell>
                      <CheckboxInput
                        position="justify-center"
                        name="delete"
                        checked={row.delete}
                        onChange={(e) => handleCheckboxChange(row.id, e, idx)}
                      />
                    </Table.Cell>
                  </Table.Row>
                ))}
              </Table.Body>
            </Table>
          </div>
        </div>
        {paginatedData.length > 0 ? (
          <></>
        ) : (
          <p className="py-4">
            該当する検索結果が見つかりませんでした。別の検索条件をお試しください。
          </p>
        )}
        <div className="w-full md:mt-3 xl:mt-6">
          <div className="xl:flex xl:items-center xl:space-x-2">
            <ButtonAdd onClick={handleClickAddButton}>追加</ButtonAdd>
            <div className="xl:mt-0 mt-3 w-full">
              <Pagination
                setPage={setCurrentPage}
                currentPage={currentPage}
                setPageSize={setPageSize}
                pageSize={pageSize}
                totalCount={totalItems}
              />
            </div>
          </div>
        </div>
        <div className="machine-update__btn--group w-full space-x-[80px] lg:space-x-[160px]">
          <ButtonReset
            onClick={handleButtonReset}
            className="lg:w-[280px] w-[240px]"
          />
          <ButtonUpdate
            onClick={handleSubmitUpdate}
            disabled={
              !(Object.keys(updateData).length > 0 || deleteIds.length > 0)
            }
            id={BUTTON_UPDATE_MACHINE_UPDATE}
            className="lg:w-[280px] w-[240px]"
          >
            <span>適用</span>
          </ButtonUpdate>
        </div>
        <MachineAddModal
          openModal={showAddModal}
          setOpenModal={setShowAddModal}
          settings={groupSettings}
          groupsList={groupsList}
          groupsChildren={groupsChildren}
          addData={addData}
          setAddData={setAddData}
          submitHandler={handleSubmitAdd}
          errors={errorsModal}
        >
          {isModalMessage ? renderMessages() : null}
        </MachineAddModal>
        <ConfirmModal
          openModal={isConfirmModalOpen}
          setOpenModal={setIsConfirmModalOpen}
          onConfirm={() => (window.location.href = MACHINE_MASTER_UPDATE)}
          setHasChanged={setHasChanged}
        />
      </div>
    </BackendLayout>
  );
}
